import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { usePostAnnualFeePaidApi } from '@/api/payment';
import { OutlineButton, PrimaryButton } from '@/components/common/Button/style';
import ModalBox from '@/components/common/Modal/ModalBox/ModalBox';
import ModalPortal from '@/components/common/Modal/ModalPortal/ModalPortal';
import PaymentModal from '@/components/common/Modal/PaymentModal/PaymentModal';
import SingleArrowNext from '@/components/Icon/SingleArrowNext';
import { CONF_TITLE } from '@/constants/menu';
import { PAYMENT_GOODS_PUBLICATION_FEE } from '@/constants/payment';
import { normalizePublicationMenu, PUBLICATION_MENU_ARTICLE_PAYMENT, PUBLICATION_STEP_CONFIRM } from '@/constants/publication';
import { useModalStore } from '@/store/modalStore';
import { useTokenStore } from '@/store/tokenStore';

import * as S from './style';

const PublicationArticlePayment = () => {
	const { t } = useTranslation();
	const START_DATE = '2026-04-27T00:00:00+09:00';
	const END_DATE = '2026-05-08T00:00:00+09:00';

	const { showModal, closeModal } = useModalStore();
	const { isLogin } = useTokenStore();
	const [isOpen, setIsOpen] = useState(false);
	const [isRegistering, setIsRegistering] = useState(false);
	const [memberType, setMemberType] = useState('');
	const navigate = useNavigate();
	const location = useLocation();
	const params = location.state?.menu ? normalizePublicationMenu(location.state.menu) : PUBLICATION_MENU_ARTICLE_PAYMENT;
	const paymentGuideLines = t('publication.articlePayment.guideLines', { returnObjects: true }) as string[];
	const { mutateAsync: annualFeePaid } = usePostAnnualFeePaidApi();

	const validateRegistrationPeriod = () => {
		const now = new Date();
		const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
		const startDate = new Date(START_DATE);
		const deadline = new Date(END_DATE);

		if (koreaTime < startDate) {
			showModal(t('publication.articlePayment.periodNotStarted'), 'confirm');
			return false;
		}

		if (koreaTime >= deadline) {
			showModal(t('publication.articlePayment.periodClosed'), 'confirm');
			return false;
		}

		return true;
	};

	const handleRegister = async () => {
		if (isRegistering) {
			return;
		}

		if (!validateRegistrationPeriod()) {
			return;
		}

		if (!isLogin()) {
			showModal(t('common.alerts.loginRequired'), 'login', t('common.actions.goToLogin'), () => navigate('/auth/login'));
			return;
		}

		setIsRegistering(true);
		try {
			const res = await annualFeePaid({
				goods_name: CONF_TITLE,
			});
			if (res.data.success) {
				setMemberType(res.data?.data?.member_type);
				setIsOpen(true);
			} else if (res.data?.error.code === '80') {
				showModal(t('publication.articlePayment.alreadyPaid'), 'login', t('common.actions.confirm'), closeModal);
			} else {
				showModal(t('publication.articlePayment.failed'), 'register');
			}
		} finally {
			setIsRegistering(false);
		}
	};

	const handleConfirm = () => {
		if (isLogin()) {
			navigate('/publication', { state: { step: PUBLICATION_STEP_CONFIRM, menu: params } });
		} else {
			showModal(t('common.alerts.loginRequired'), 'login', t('common.actions.goToLogin'), () => navigate('/auth/login'));
			return;
		}
	};

	return (
		<S.Container>
			<S.ContentBox>
				<S.ContentTitle>{t('publication.articlePayment.guideTitle')}</S.ContentTitle>
				<S.BasicInfoButtonBox>
					<PrimaryButton type="button" onClick={handleRegister} disabled={isRegistering}>
						{t('common.actions.pay')}
						<SingleArrowNext iconColor="currentColor" width="20" height="20" />
					</PrimaryButton>
					<OutlineButton type="button" onClick={handleConfirm}>
						{t('publication.articlePayment.confirmButton')}
						<SingleArrowNext iconColor="currentColor" width="20" height="20" />
					</OutlineButton>
				</S.BasicInfoButtonBox>
				<S.ContentDescription>
					{paymentGuideLines.map((line) => (
						<li key={line}>{line}</li>
					))}
				</S.ContentDescription>
			</S.ContentBox>
			<S.ContentBox>
				<S.ContentBox>
					<S.ContentTitle>{t('publication.articlePayment.feeTitle')}</S.ContentTitle>
					<S.TablePanel>
						<S.Table>
							<S.TableHeadCell className="item2 center">{t('publication.articlePayment.feeTable.headers.type')}</S.TableHeadCell>
							<S.TableHeadCell className="center">{t('publication.articlePayment.feeTable.headers.fee')}</S.TableHeadCell>

							<S.TableCell className="item3 point center">{t('publication.articlePayment.feeTable.unit')}</S.TableCell>
							<S.TableCell className="min-height point center">{t('publication.articlePayment.feeTable.basic')}</S.TableCell>
							<S.TableCell className="min-height center">{t('publication.articlePayment.feeTable.basicPrice')}</S.TableCell>

							<S.TableCell className="min-height point center">{t('publication.articlePayment.feeTable.express')}</S.TableCell>
							<S.TableCell className="min-height center">{t('publication.articlePayment.feeTable.expressPrice')}</S.TableCell>
						</S.Table>
					</S.TablePanel>
				</S.ContentBox>
			</S.ContentBox>
			{isOpen && (
				<ModalPortal>
					<ModalBox onClick={() => setIsOpen(false)}>
						<PaymentModal closeModal={setIsOpen} goodsName={PAYMENT_GOODS_PUBLICATION_FEE} memberType={memberType} />
					</ModalBox>
				</ModalPortal>
			)}
		</S.Container>
	);
};

export default PublicationArticlePayment;
